import { downloadFileText, getFileMetadata } from "../drive-api";
import {
  getCachedHtml,
  getFile,
  setCachedHtml,
  updateModifiedTime,
  type TrackedFile,
} from "../store";

const POLL_INTERVAL_MS = 30_000;

type Status = "ok" | "loading" | "error";

function escapeHtml(s: string): string {
  return s
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function renderShell(name: string): string {
  return `
    <div class="viewer">
      <iframe
        class="viewer-frame"
        sandbox="allow-scripts allow-popups allow-forms"
        referrerpolicy="no-referrer"
        title="Conteúdo do HTML"
      ></iframe>
      <div class="viewer-hover-zone" data-role="hover-zone" aria-hidden="true"></div>
      <header class="viewer-bar hidden" data-role="bar">
        <a class="viewer-back" href="#/" aria-label="Voltar">←</a>
        <div class="viewer-title" title="${escapeHtml(name)}">${escapeHtml(name)}</div>
        <span class="dot ok" data-role="indicator" title="Atualizado"></span>
        <button class="viewer-refresh" data-action="refresh" title="Atualizar agora" aria-label="Atualizar">⟳</button>
      </header>
    </div>
  `;
}

function setIndicator(root: HTMLElement, status: Status, title: string): void {
  const dot = root.querySelector<HTMLElement>('[data-role="indicator"]');
  if (!dot) return;
  dot.className = `dot ${status === "ok" ? "ok" : status === "loading" ? "loading" : "err"}`;
  dot.title = title;
}

function setFrameContent(root: HTMLElement, html: string): void {
  const frame = root.querySelector<HTMLIFrameElement>(".viewer-frame");
  if (!frame) return;
  frame.srcdoc = html;
}

export function mountViewerView(root: HTMLElement, fileId: string): () => void {
  const tracked: TrackedFile | null = getFile(fileId);
  if (!tracked) {
    root.innerHTML = `
      <div class="empty">
        <p>Arquivo não encontrado na lista.</p>
        <p><a href="#/">← Voltar</a></p>
      </div>
    `;
    return () => {};
  }

  root.innerHTML = renderShell(tracked.name);

  let disposed = false;
  let inflight = false;

  async function applyCachedIfAny(): Promise<boolean> {
    const cached = await getCachedHtml(fileId);
    if (cached && !disposed) {
      setFrameContent(root, cached.html);
      setIndicator(root, "ok", `Em cache (${new Date(cached.fetchedAt).toLocaleString("pt-BR")})`);
      return true;
    }
    return false;
  }

  async function checkAndRefresh(force = false): Promise<void> {
    if (disposed || inflight) return;
    inflight = true;
    setIndicator(root, "loading", "Verificando atualização…");
    try {
      const meta = await getFileMetadata(fileId);
      const current = getFile(fileId);
      const cached = await getCachedHtml(fileId);
      const changed =
        !cached || cached.modifiedTime !== meta.modifiedTime || (current && current.modifiedTime !== meta.modifiedTime);
      if (force || changed) {
        const html = await downloadFileText(fileId);
        if (disposed) return;
        await setCachedHtml(fileId, {
          html,
          modifiedTime: meta.modifiedTime,
          fetchedAt: Date.now(),
        });
        updateModifiedTime(fileId, meta.modifiedTime, meta.name);
        setFrameContent(root, html);
        setIndicator(root, "ok", `Atualizado ${new Date().toLocaleString("pt-BR")}`);
      } else {
        setIndicator(root, "ok", `Sem mudanças (verificado ${new Date().toLocaleTimeString("pt-BR")})`);
      }
    } catch (err) {
      if (!disposed) {
        setIndicator(root, "error", `Erro: ${(err as Error).message}`);
      }
    } finally {
      inflight = false;
    }
  }

  function onClick(ev: Event): void {
    const target = ev.target as HTMLElement;
    const action = target.closest<HTMLElement>("[data-action]")?.dataset.action;
    if (action === "refresh") void checkAndRefresh(true);
  }

  root.addEventListener("click", onClick);

  // ---- Auto-hide da barra ----
  const bar = root.querySelector<HTMLElement>('[data-role="bar"]')!;
  const hoverZone = root.querySelector<HTMLElement>('[data-role="hover-zone"]')!;
  let hideTimer: ReturnType<typeof setTimeout> | undefined;
  const HIDE_AFTER_MS = 2000;

  function showBar(): void {
    bar.classList.remove("hidden");
    if (hideTimer) clearTimeout(hideTimer);
    hideTimer = setTimeout(() => bar.classList.add("hidden"), HIDE_AFTER_MS);
  }

  // Mostra brevemente na primeira carga pra dar pista visual de que existe.
  showBar();

  hoverZone.addEventListener("mouseenter", showBar);
  bar.addEventListener("mouseenter", showBar);
  bar.addEventListener("mousemove", showBar);
  // Mobile: tap no topo (zona) reabre.
  hoverZone.addEventListener("touchstart", showBar, { passive: true });

  let intervalId: ReturnType<typeof setInterval> | undefined;

  function startPolling(): void {
    if (intervalId) return;
    intervalId = setInterval(() => {
      if (document.hidden) return;
      void checkAndRefresh(false);
    }, POLL_INTERVAL_MS);
  }

  function stopPolling(): void {
    if (intervalId) clearInterval(intervalId);
    intervalId = undefined;
  }

  function onVisibility(): void {
    if (document.hidden) return;
    void checkAndRefresh(false);
  }

  // Boot: tenta exibir cache imediatamente, depois valida em background.
  void (async () => {
    const hadCache = await applyCachedIfAny();
    if (!hadCache) {
      setIndicator(root, "loading", "Baixando…");
    }
    await checkAndRefresh(!hadCache);
    startPolling();
  })();

  document.addEventListener("visibilitychange", onVisibility);

  return () => {
    disposed = true;
    stopPolling();
    document.removeEventListener("visibilitychange", onVisibility);
    root.removeEventListener("click", onClick);
  };
}
